import React, { useEffect, useState } from "react";
import {
  captureReactError,
  dismissReport,
  downloadReport,
  getPendingReport,
  submitReport,
} from "../lib/diagnostics";
import { useAuth } from "../context/AuthContext";

export class DiagnosticBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    captureReactError(error, info?.componentStack || "");
  }

  render() {
    if (this.state.failed) {
      return (
        <main
          role="alert"
          className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white p-6"
        >
          <div className="w-full max-w-xl rounded-xl border border-white/20 bg-slate-900 p-6 shadow-2xl">
            <h1 className="text-2xl font-bold">The application encountered an error</h1>
            <p className="mt-3 text-slate-200">
              Technical details were preserved on this device. Reload the page to continue and review the report.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded bg-royal-blue px-5 py-3 font-semibold text-white"
                onClick={() => window.location.reload()}
              >
                Reload
              </button>
              <button
                type="button"
                className="rounded border border-slate-500 px-5 py-3 font-semibold text-white"
                onClick={async () => {
                  const report = getPendingReport();
                  if (!report) return;
                  try {
                    const receipt = await submitReport(report);
                    window.alert(`Technical report sent: ${receipt?.report_id || report.client_report_id}`);
                  } catch {
                    window.alert("Report could not be sent. It remains saved on this device for retrieval after reload.");
                  }
                }}
              >
                Send Technical Report
              </button>
              <button
                type="button"
                className="rounded border border-slate-500 px-5 py-3 font-semibold text-white"
                onClick={async () => {
                  const report = getPendingReport();
                  if (report) await downloadReport(report);
                }}
              >
                Download Report ZIP
              </button>
            </div>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

export default function DiagnosticRecovery() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [report, setReport] = useState(() => getPendingReport());
  const [receipt, setReceipt] = useState(report?.receipt || null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const refresh = () => {
      const next = getPendingReport();
      setReport(next);
      setReceipt(next?.receipt || null);
    };
    window.addEventListener("ysq:diagnostic-change", refresh);
    refresh();
    return () => window.removeEventListener("ysq:diagnostic-change", refresh);
  }, []);

  if (!report) return null;

  const uncertain = report.kind === "unexpected_session_end";

  async function send(downloadAfter) {
    setBusy(true);
    setMessage("");
    try {
      const result = receipt || await submitReport(report);
      setReceipt(result);
      if (downloadAfter) await downloadReport({ ...report, receipt: result });
      setMessage(downloadAfter ? "Report received and ZIP prepared." : "Report received.");
    } catch {
      setMessage("The report service could not be reached. The report remains stored on this device.");
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    dismissReport(report.client_report_id);
  }

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto overscroll-contain bg-black/75 p-2 sm:p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) dismiss(); }}>
      <div className="flex min-h-full items-start justify-center py-2 sm:items-center sm:py-4">
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="diagnostic-title"
          className="relative w-full max-w-xl max-h-[calc(100vh-1rem)] max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain rounded-xl border border-white/20 bg-slate-950 p-4 text-white shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:p-6"
        >
        <div className="sticky top-0 z-30 -mt-2 mb-1 flex justify-end pointer-events-none">
          <button
            type="button"
            aria-label="Close technical diagnostics"
            onClick={dismiss}
            className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-slate-900/95 text-2xl font-bold text-white shadow-xl"
          >
            ×
          </button>
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-kingdom-gold">
          Technical Diagnostics
        </p>
        <h2 id="diagnostic-title" className="mt-2 text-2xl font-bold">
          {uncertain ? "Previous session ended unexpectedly" : "Application error detected"}
        </h2>
        <p className="mt-3 text-sm text-slate-200">
          {uncertain
            ? "Your previous session ended unexpectedly. You can send a technical report to help us investigate what happened."
            : "The application encountered an error. You can send a technical report to help us investigate it."}
        </p>

        <details className="mt-3 text-xs text-slate-300">
          <summary className="cursor-pointer font-semibold text-slate-200">What is included in the report?</summary>
          <p className="mt-2 leading-5">
            Technical error details, the page path, browser/device information, recent navigation,
            and failed request paths. It does not intentionally include messages, form entries,
            meeting content, passwords, authentication tokens, recovery keys, or encryption keys.
          </p>
        </details>

        {isAdmin ? (
          <div className="mt-4 rounded border border-white/15 bg-white/5 p-3 text-xs">
            <div><span className="font-semibold">Type:</span> {report.kind}</div>
            <div className="mt-1">
              <span className="font-semibold">Page:</span>{" "}
              <span className="break-all [overflow-wrap:anywhere]">{report.route || "unknown"}</span>
            </div>
            <div className="mt-1 break-all">
              <span className="font-semibold">Report:</span> {receipt?.report_id || report.client_report_id}
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded border border-white/15 bg-white/5 p-3 text-xs">
            <span className="font-semibold">Report ID:</span>{" "}
            <span className="break-all">{receipt?.report_id || report.client_report_id}</span>
          </div>
        )}

        {message && <p className="mt-4 text-sm text-slate-200">{message}</p>}

        {receipt && (
          <div className="mt-4 rounded border border-kingdom-gold/40 bg-kingdom-gold/10 p-3 text-sm">
            {isAdmin ? (
              <>
                <p className="font-semibold text-kingdom-gold">
                  Report received and stored in Technical Diagnostics.
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-200">
                  You can review it in Admin → Diagnostics.
                </p>
                <a
                  href="/admin/diagnostics"
                  className="mt-3 inline-flex rounded border border-kingdom-gold/60 px-3 py-2 text-xs font-semibold text-kingdom-gold"
                >
                  Open Technical Reports
                </a>
              </>
            ) : (
              <>
                <p className="font-semibold text-kingdom-gold">Report sent.</p>
                <p className="mt-1 text-xs leading-5 text-slate-200">
                  The technical team can use the report ID above to find it. You can close this window.
                </p>
              </>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            disabled={busy}
            onClick={() => send(true)}
            className="w-full rounded bg-royal-blue px-4 py-3 font-semibold text-white disabled:opacity-50 sm:w-auto sm:py-2"
          >
            {busy ? "Preparing…" : receipt ? "Download ZIP" : "Send & Download ZIP"}
          </button>
          {!receipt && (
            <button
              type="button"
              disabled={busy}
              onClick={() => send(false)}
              className="w-full rounded border border-slate-400 px-4 py-3 disabled:opacity-50 sm:w-auto sm:py-2"
            >
              Send Report
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={dismiss}
            className="w-full rounded border border-slate-400 px-4 py-3 disabled:opacity-50 sm:w-auto sm:py-2"
          >
            {receipt ? "Close" : "Not Now"}
          </button>
        </div>
        </section>
      </div>
    </div>
  );
}
